import { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRightIcon,
    CheckIcon,
    ChevronRightIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SparklesIcon,
    BoltIcon,
} from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import IndustryDemo from "../components/landing/IndustryDemo";
import {
    getRelatedIndustries,
    INDUSTRY_USE_CASES_BY_SLUG,
    WIDGET_TYPE_BADGE,
    WIDGET_TYPE_LABEL,
} from "../data/industryUseCases";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { buildIndustryFaqs } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";
import { signupCampaignBySlug, signupPathFor } from "../data/signupCampaigns";
import { comOnlyInboundGuideLink } from "../data/seo/inboundVoiceGuide";
import { comMarketing, industryKw3, KW3_HOTEL_BLOG_PATH } from "../data/seo/kw3OnPage";

/**
 * Per-industry landing page (e.g. /use-cases/auto-dealers).
 * Renders the marketing hero + animated voice/chat demo (with sound) +
 * features grid + pipeline + recommended outbound triggers + related-
 * industry cards, all driven by `INDUSTRY_USE_CASES_BY_SLUG`.
 *
 * SEO contract:
 *   - Per-page <title>/description from industry.seo (managed by usePageSeo).
 *   - BreadcrumbList JSON-LD via <Breadcrumb> for the SERP rich result.
 *   - Service JSON-LD describing this industry pack (provider = Organization).
 *   - FAQPage JSON-LD via <FAQSection>, populated from buildIndustryFaqs.
 */

const Eyebrow = ({ accent, children }: { accent: string; children: React.ReactNode }) => (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
        <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${accent}`} />
        {children}
    </div>
);

/** Gradient the phrase after the last " for " so a custom H1 keeps the hero accent. */
function HeroHeading({ headline, accent, shortLabel }: { headline?: string; accent: string; shortLabel: string }) {
    const accentClass = `bg-gradient-to-r ${accent} bg-clip-text text-transparent`;
    const text = headline ?? `AI Voice + Chat for ${shortLabel}`;
    const marker = " for ";
    const splitAt = text.lastIndexOf(marker);
    if (splitAt === -1) return <>{text}</>;
    return (
        <>
            {text.slice(0, splitAt + marker.length)}
            <span className={accentClass}>{text.slice(splitAt + marker.length)}</span>
        </>
    );
}

export default function IndustryUseCasePage() {
    const { industrySlug } = useParams<{ industrySlug: string }>();
    const industry = industrySlug ? INDUSTRY_USE_CASES_BY_SLUG[industrySlug] : undefined;

    const relatedIndustries = useMemo(
        () => (industry ? getRelatedIndustries(industry, 3) : []),
        [industry],
    );

    // This page ranks for the vertical; its signup page converts for it. Send
    // the CTA to the vertical's own form rather than the generic `/signup`,
    // which would discard the industry the visitor just self-selected.
    const startFreePath = useMemo(() => {
        const campaign = industry ? signupCampaignBySlug(industry.slug) : null;
        return campaign ? signupPathFor(campaign) : "/signup";
    }, [industry]);

    const kw3 = industryKw3(industry?.slug);

    usePageSeo({
        title: kw3?.title ?? industry?.seo.title,
        description: kw3?.description ?? industry?.seo.description,
        keywords: industry?.seo.keywords.join(", "),
        canonical: industry ? `/use-cases/${industry.slug}` : undefined,
    });

    const inboundSetupGuide = industry?.slug === "general-support" ? comOnlyInboundGuideLink() : null;

    const breadcrumbSchema = useMemo(() => {
        if (!industry) return null;
        return {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Use Cases", item: `${marketingOrigin()}/use-cases` },
                {
                    "@type": "ListItem",
                    position: 3,
                    name: industry.shortLabel,
                    item: `${marketingOrigin()}/use-cases/${industry.slug}`,
                },
            ],
        };
    }, [industry]);

    const serviceSchema = useMemo(() => {
        if (!industry) return null;
        return buildServiceSchema({
            name: `FloGPT for ${industry.shortLabel}`,
            description: `${industry.tagline} ${industry.description}`,
            path: `/use-cases/${industry.slug}`,
            serviceType: `AI Voice & Chat Agents — ${industry.shortLabel}`,
        });
    }, [industry]);

    const faqs = useMemo(
        () => (industry ? buildIndustryFaqs(industry.shortLabel, industry.callWidgetType) : []),
        [industry],
    );

    // Scroll to top on route change so each industry page starts at hero.
    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [industrySlug]);

    if (!industry) {
        return <Navigate to="/use-cases" replace />;
    }

    const Icon = industry.Icon;

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                {/* Hero */}
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className={`absolute -left-32 top-10 h-72 w-72 rounded-full blur-3xl ${industry.softAccent}`} />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-pink-500/10 blur-3xl" />
                        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12 lg:py-20">
                        {/* Breadcrumb */}
                        <nav aria-label="Breadcrumb" className="text-xs text-slate-400 flex items-center gap-1.5 mb-6">
                            <Link to="/" className="hover:text-white transition-colors">Home</Link>
                            <ChevronRightIcon className="h-3 w-3" />
                            <Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link>
                            <ChevronRightIcon className="h-3 w-3" />
                            <span className="text-slate-200">{industry.shortLabel}</span>
                        </nav>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                            {/* Left: hero copy */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="lg:col-span-6"
                            >
                                <Eyebrow accent={industry.accent}>Use Case · {industry.shortLabel}</Eyebrow>

                                <div className="mt-5 flex items-center gap-3">
                                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${industry.accent} flex items-center justify-center shadow-lg`}>
                                        <Icon className="h-6 w-6 text-white" />
                                    </div>
                                    <span
                                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${
                                            WIDGET_TYPE_BADGE[industry.callWidgetType]
                                        }`}
                                    >
                                        {WIDGET_TYPE_LABEL[industry.callWidgetType]}
                                    </span>
                                </div>

                                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                                    <HeroHeading
                                        headline={kw3?.headline ?? industry.headline}
                                        accent={industry.accent}
                                        shortLabel={industry.shortLabel}
                                    />
                                </h1>
                                {kw3?.subhead ? (
                                    <p className="mt-3 text-lg font-medium text-slate-200">{kw3.subhead}</p>
                                ) : null}
                                <p className="mt-6 text-lg leading-relaxed text-slate-300 max-w-xl">
                                    {kw3?.lead ?? industry.tagline}
                                </p>
                                <p className="mt-4 text-base leading-relaxed text-slate-400 max-w-xl">
                                    {industry.description}
                                </p>
                                {inboundSetupGuide ? (
                                    <p className="mt-4 text-sm">
                                        <Link
                                            to={inboundSetupGuide.href}
                                            className="font-medium text-orange-400 hover:text-orange-300"
                                        >
                                            {inboundSetupGuide.label}
                                        </Link>
                                    </p>
                                ) : null}

                                <div className="mt-8 flex flex-wrap items-center gap-3">
                                    <Link
                                        to={startFreePath}
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        Start free
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Talk to sales
                                    </Link>
                                </div>

                                <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                                    <div className="inline-flex items-center gap-1.5">
                                        <PhoneIcon className="h-4 w-4 text-emerald-400" />
                                        Inbound + outbound voice
                                    </div>
                                    <div className="inline-flex items-center gap-1.5">
                                        <ChatBubbleLeftRightIcon className="h-4 w-4 text-orange-400" />
                                        Embeddable web chat
                                    </div>
                                    <div className="inline-flex items-center gap-1.5">
                                        <BoltIcon className="h-4 w-4 text-yellow-400" />
                                        Live in &lt; 5 minutes
                                    </div>
                                </div>

                                {industry.slug === "hospitality" && comMarketing() ? (
                                    <p className="mt-4 text-sm text-slate-400">
                                        Front-desk calls are covered in the{" "}
                                        <Link to={KW3_HOTEL_BLOG_PATH} className="font-medium text-orange-400 hover:text-orange-300">
                                            hotel phone bot
                                        </Link>{" "}
                                        article.
                                    </p>
                                ) : null}
                                <p className="mt-6 text-sm text-slate-400">
                                    Powered by FloGPT's{" "}
                                    <Link to="/ai-voice-agents" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                        AI voice agents
                                    </Link>
                                    ,{" "}
                                    <Link to="/ai-chat-widget" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                        {industry.slug === "retail" && comMarketing() ? "AI chat widget for online stores" : "AI chat widget"}
                                    </Link>{" "}
                                    and{" "}
                                    <Link to="/ai-crm" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                        unified AI CRM
                                    </Link>
                                    .
                                </p>
                            </motion.div>

                            {/* Right: animated demo */}
                            <motion.div
                                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                                className="lg:col-span-6"
                            >
                                <IndustryDemo industry={industry} initialMode="voice" />
                                <p className="mt-3 text-[11px] text-slate-500 text-center">
                                    Toggle between voice and chat to see how the same agent handles both. Click "Play sound" to enable the audio cues.
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {kw3?.section ? (
                    <section className="relative overflow-hidden">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-4">
                            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                {kw3.section.h2Link && kw3.section.h2.includes(kw3.section.h2Link.phrase) ? (
                                    <>
                                        {kw3.section.h2.slice(0, kw3.section.h2.indexOf(kw3.section.h2Link.phrase))}
                                        <Link
                                            to={kw3.section.h2Link.href}
                                            className="text-orange-300 hover:text-orange-200"
                                        >
                                            {kw3.section.h2Link.phrase}
                                        </Link>
                                        {kw3.section.h2.slice(
                                            kw3.section.h2.indexOf(kw3.section.h2Link.phrase) + kw3.section.h2Link.phrase.length,
                                        )}
                                    </>
                                ) : (
                                    kw3.section.h2
                                )}
                            </h2>
                            <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300">
                                {kw3.section.bodyLink && kw3.section.body.includes(kw3.section.bodyLink.phrase) ? (
                                    <>
                                        {kw3.section.body.slice(0, kw3.section.body.indexOf(kw3.section.bodyLink.phrase))}
                                        <Link
                                            to={kw3.section.bodyLink.href}
                                            className="font-medium text-orange-400 hover:text-orange-300"
                                        >
                                            {kw3.section.bodyLink.phrase}
                                        </Link>
                                        {kw3.section.body.slice(
                                            kw3.section.body.indexOf(kw3.section.bodyLink.phrase) +
                                                kw3.section.bodyLink.phrase.length,
                                        )}
                                    </>
                                ) : (
                                    kw3.section.body
                                )}
                            </p>
                        </div>
                    </section>
                ) : null}

                {/* What it does */}
                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="max-w-3xl"
                        >
                            <Eyebrow accent={industry.accent}>What FloGPT does for {industry.shortLabel}</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                Five high-impact moves, working out of the box
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed">
                                Every vertical pack ships with prompts, scripts, intent gates and post-call triggers
                                pre-tuned for the way {industry.shortLabel.toLowerCase()} businesses actually run.
                            </p>
                        </motion.div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {industry.features.map((feature, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${industry.accent} flex items-center justify-center shrink-0`}>
                                            <CheckIcon className="h-4 w-4 text-white" />
                                        </div>
                                        <p className="text-slate-200 leading-relaxed">{feature}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why FloGPT */}
                <section className="relative overflow-hidden">
                    <div className={`absolute inset-0 -z-10 ${industry.softAccent} opacity-40`} />
                    <div className="absolute inset-0 -z-10 bg-slate-900/80" />
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="max-w-3xl"
                        >
                            <Eyebrow accent={industry.accent}>Why FloGPT</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                Built for the way {industry.shortLabel.toLowerCase()} actually works
                            </h2>
                        </motion.div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {industry.whyFloGPT.map((reason, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    className="relative rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-sm p-6"
                                >
                                    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${industry.accent} flex items-center justify-center shadow-lg mb-4`}>
                                        <SparklesIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{reason.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{reason.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Pipeline */}
                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="max-w-3xl mb-12"
                        >
                            <Eyebrow accent={industry.accent}>Call Pipeline</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                The {industry.shortLabel.toLowerCase()} pipeline, visually
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed">
                                Every conversation follows a clear flow. You can edit nodes, branch on intent,
                                add tools and re-publish — all from the visual Call Pipeline editor.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 sm:p-10"
                        >
                            <div className={`absolute -inset-2 rounded-3xl blur-2xl ${industry.softAccent}`} />
                            <div className="relative flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                                {industry.pipelineSteps.map((step, idx, arr) => (
                                    <div key={step} className="flex items-center gap-4 sm:gap-6">
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.35, delay: idx * 0.08 }}
                                            viewport={{ once: true }}
                                            className={`rounded-xl bg-gradient-to-br ${industry.accent} px-5 py-3 text-white font-semibold shadow-lg text-sm sm:text-base`}
                                        >
                                            {step}
                                        </motion.div>
                                        {idx < arr.length - 1 && (
                                            <span className="text-slate-500 text-2xl">→</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Recommended outbound triggers */}
                {industry.recommendedTriggers.length > 0 && (
                    <section className="relative overflow-hidden">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="max-w-3xl"
                            >
                                <Eyebrow accent={industry.accent}>Outbound · Pre-built triggers</Eyebrow>
                                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    The agent doesn't just answer — it dials out, too
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed">
                                    Each pack ships with a few outbound triggers tuned for the vertical. They're
                                    disabled by default — review and enable from the dashboard when you're ready.
                                </p>
                            </motion.div>

                            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                                {industry.recommendedTriggers.map((trigger, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: idx * 0.06 }}
                                        viewport={{ once: true, amount: 0.3 }}
                                        className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${industry.accent} flex items-center justify-center shrink-0`}>
                                                <PhoneIcon className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="text-base font-semibold text-white">{trigger.name}</h3>
                                                <p className="mt-1 text-sm text-slate-400 leading-relaxed">
                                                    {trigger.description}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Related industries */}
                {relatedIndustries.length > 0 && (
                    <section className="relative overflow-hidden">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true, amount: 0.3 }}
                                className="flex flex-wrap items-end justify-between gap-4 mb-10"
                            >
                                <div className="max-w-xl">
                                    <Eyebrow accent={industry.accent}>Other use cases</Eyebrow>
                                    <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                        Browse other vertical packs
                                    </h2>
                                </div>
                                <Link
                                    to="/use-cases"
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors"
                                >
                                    See all 13 industries
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                            </motion.div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {relatedIndustries.map((rel, idx) => {
                                    const RelIcon = rel.Icon;
                                    return (
                                        <motion.div
                                            key={rel.key}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4, delay: idx * 0.06 }}
                                            viewport={{ once: true, amount: 0.3 }}
                                        >
                                            <Link
                                                to={`/use-cases/${rel.slug}`}
                                                className="group relative block rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all h-full"
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${rel.accent} flex items-center justify-center shadow-lg`}>
                                                        <RelIcon className="h-5 w-5 text-white" />
                                                    </div>
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r ${
                                                            WIDGET_TYPE_BADGE[rel.callWidgetType]
                                                        } bg-clip-text text-transparent`}
                                                    >
                                                        {WIDGET_TYPE_LABEL[rel.callWidgetType]}
                                                    </span>
                                                </div>
                                                <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors">
                                                    {rel.shortLabel}
                                                </h3>
                                                <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-3">
                                                    {rel.tagline}
                                                </p>
                                                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                                                    Explore
                                                    <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </Link>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}

                {/* Final CTA */}
                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-20">
                        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-10 sm:p-14 text-center overflow-hidden">
                            <div className={`absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full blur-3xl ${industry.softAccent}`} />
                            <div className="relative">
                                <Eyebrow accent={industry.accent}>Ready to deploy?</Eyebrow>
                                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    Spin up the {industry.shortLabel} pack in minutes
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl mx-auto">
                                    Sign up free, attach a phone number or paste the chat-widget script,
                                    and pick this pack on the dashboard. The agent's already trained.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                                    <Link
                                        to={startFreePath}
                                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                    >
                                        Start free
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Book a 15-min demo
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <FAQSection
                    id={`industry-${industry.slug}`}
                    eyebrow={`${industry.shortLabel} · FAQ`}
                    heading={`${industry.shortLabel} FAQ`}
                    intro={`Common questions about deploying FloGPT in ${industry.shortLabel.toLowerCase()} businesses.`}
                    items={faqs}
                />
            </main>

            <Footer />

            {breadcrumbSchema && <JsonLd id={`breadcrumb-industry-${industry.slug}`} data={breadcrumbSchema} />}
            {serviceSchema && <JsonLd id={`service-industry-${industry.slug}`} data={serviceSchema} />}
        </div>
    );
}
